/**
 * Seo — sets the per-route `<title>`, description, canonical and Open Graph tags.
 *
 * WHY NOT `react-helmet-async` (s216, decision recorded rather than assumed). The marketing ask
 * suggested it "or the equivalent already in the repo". There is no equivalent in the repo, and
 * adding one is expensive HERE specifically: CLAUDE.md § Gotchas forbids committing a lockfile
 * produced by a local `npm install` — a dependency add has to go through the Linux Lockfile
 * Action — and a lockfile change moves this build into the FULL staging gate (D-195.1). The whole
 * job is "write four tags into `<head>` and put them back on unmount", which is the 60 lines
 * below. So: no dependency, no lockfile churn, and this stays in the light lane.
 *
 * 🔴 WHAT THIS DOES NOT FIX, stated plainly so it is not mistaken for the real thing: these tags
 * are written by JavaScript AFTER the bundle loads. A crawler that executes JS will see them; one
 * that reads the raw HTML will still see `client/index.html`'s defaults. Per-route metadata is
 * only genuinely fixed by PRE-RENDERING, which is the phase-2 question. This component is the
 * half that can ship today, and it is what makes the pre-render a drop-in later: the titles and
 * descriptions already live in `shared/constants/public-routes.ts`, so a prerender step reads the
 * same declaration instead of a new one.
 */
import { useEffect } from 'react';
import { absoluteUrl, publicRouteFor, type PublicRoute } from '@shared/constants/public-routes';

/** Sets (or creates) a `<meta>` tag by name/property and returns a restore function. */
function setMeta(attr: 'name' | 'property', key: string, value: string): () => void {
  const selector = `meta[${attr}="${CSS.escape(key)}"]`;
  const existing = document.head.querySelector<HTMLMetaElement>(selector);

  if (existing) {
    const previous = existing.getAttribute('content');
    existing.setAttribute('content', value);
    return () => {
      if (previous === null) existing.removeAttribute('content');
      else existing.setAttribute('content', previous);
    };
  }

  const created = document.createElement('meta');
  created.setAttribute(attr, key);
  created.setAttribute('content', value);
  document.head.appendChild(created);
  return () => created.remove();
}

/** Sets (or creates) `<link rel="canonical">` and returns a restore function. */
function setCanonical(href: string): () => void {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (existing) {
    const previous = existing.getAttribute('href');
    existing.setAttribute('href', href);
    return () => {
      if (previous === null) existing.removeAttribute('href');
      else existing.setAttribute('href', previous);
    };
  }
  const created = document.createElement('link');
  created.setAttribute('rel', 'canonical');
  created.setAttribute('href', href);
  document.head.appendChild(created);
  return () => created.remove();
}

export interface SeoProps {
  /** A declared public route path — its title/description come from PUBLIC-ROUTES-1. */
  path: string;
  /** Overrides, for a page whose metadata is not route-derived (e.g. the 404). */
  title?: string;
  description?: string;
  /** `true` adds `<meta name="robots" content="noindex">` (the 404 page). */
  noindex?: boolean;
}

/**
 * Applies head tags for a route. Renders nothing.
 *
 * 🔴 Every tag it touches is RESTORED on unmount, so navigating from `/pricing` back to `/`
 * does not leave the pricing title behind — the reason each setter returns an undo.
 */
export function Seo({ path, title, description, noindex = false }: SeoProps) {
  const route: PublicRoute | undefined = publicRouteFor(path);
  const resolvedTitle = title ?? route?.title;
  const resolvedDescription = description ?? route?.description;

  useEffect(() => {
    const undo: Array<() => void> = [];

    if (resolvedTitle) {
      const previousTitle = document.title;
      document.title = resolvedTitle;
      undo.push(() => {
        document.title = previousTitle;
      });
      undo.push(setMeta('property', 'og:title', resolvedTitle));
    }

    if (resolvedDescription) {
      undo.push(setMeta('name', 'description', resolvedDescription));
      undo.push(setMeta('property', 'og:description', resolvedDescription));
    }

    const url = absoluteUrl(path);
    undo.push(setCanonical(url));
    undo.push(setMeta('property', 'og:url', url));

    if (noindex) {
      undo.push(setMeta('name', 'robots', 'noindex'));
    }

    // Run the undos in reverse so a tag this effect CREATED is removed before a tag it
    // merely overwrote is restored.
    return () => {
      for (let i = undo.length - 1; i >= 0; i--) undo[i]();
    };
  }, [path, resolvedTitle, resolvedDescription, noindex]);

  return null;
}
